/**
 * CommandPalette.tsx — global ⌘K / Ctrl+K command menu (the same pattern
 * used by Linear, Vercel, Raycast and GitHub) for jumping to any screen or
 * starting a new content item without touching the sidebar. Built on cmdk
 * inside a Radix Dialog for the overlay/focus-trap.
 */
import { useEffect, useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  Braces,
  CalendarDays,
  ClipboardCheck,
  FilePlus2,
  FileText,
  Globe,
  Inbox,
  LayoutDashboard,
  Megaphone,
  ScrollText,
  Search,
  Settings,
  Settings2,
  Share2,
  Sparkles,
  Users,
  GitBranch,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, can } = useAuth();
  // Item 7's granular RBAC: only the handful of routes that are ALWAYS
  // manager-only (users, audit, git promote) stay gated on the role
  // directly — everything else reflects the signed-in user's real
  // effective capability, same reasoning as Layout.tsx's NAV filter.
  const isManager = user?.role === "manager";

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[18%] z-50 w-full max-w-lg -translate-x-1/2 overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-neutral-900/5 data-[state=open]:animate-scale-in">
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <CommandPrimitive>
            <div className="flex items-center gap-2 border-b border-neutral-100 px-4">
              <Search className="size-4 text-neutral-400" />
              <CommandPrimitive.Input
                autoFocus
                placeholder="Jump to a screen, or search…"
                className="h-12 w-full bg-transparent text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-neutral-400"
              />
              <kbd className="rounded border border-neutral-200 px-1.5 py-0.5 text-xs font-medium text-neutral-400">
                ESC
              </kbd>
            </div>
            <CommandPrimitive.List className="scrollbar-thin max-h-80 overflow-y-auto p-2">
              <CommandPrimitive.Empty className="py-8 text-center text-sm text-neutral-400">
                No matches.
              </CommandPrimitive.Empty>
              <CommandPrimitive.Group heading="Navigate" className="px-2 pb-1 pt-2 text-xs font-semibold text-neutral-400 [&_[cmdk-group-items]]:mt-1">
                <Item icon={LayoutDashboard} onSelect={() => go("/")}>Dashboard</Item>
                <Item icon={FileText} onSelect={() => go("/content")}>Content</Item>
                <Item icon={FilePlus2} onSelect={() => go("/content/new")}>New content item</Item>
                <Item icon={CalendarDays} onSelect={() => go("/calendar")}>Calendar</Item>
                <Item icon={Share2} onSelect={() => go("/social")}>Social</Item>
                {can("ai.use") && <Item icon={Megaphone} onSelect={() => go("/ads")}>Ads</Item>}
                {can("ai.use") && <Item icon={ClipboardCheck} onSelect={() => go("/ads?view=review")}>Review this week&apos;s ads</Item>}
                <Item icon={Sparkles} onSelect={() => go("/ai")}>AI &amp; Data</Item>
                {can("content.publish") && <Item icon={Inbox} onSelect={() => go("/review")}>Review</Item>}
                <Item icon={Settings} onSelect={() => go("/settings")}>Settings</Item>
                {can("taxonomy.manage") && (
                  <Item icon={Settings2} onSelect={() => go("/content/categories")}>
                    Categories & authors
                  </Item>
                )}
                {can("variables.manage") && (
                  <Item icon={Braces} onSelect={() => go("/settings/variables")}>
                    Variables
                  </Item>
                )}
                {can("sites.manage") && <Item icon={Globe} onSelect={() => go("/settings/sites")}>Sites</Item>}
                {isManager && <Item icon={GitBranch} onSelect={() => go("/git")}>Publishing</Item>}
                {isManager && <Item icon={Users} onSelect={() => go("/users")}>People</Item>}
                {isManager && <Item icon={ScrollText} onSelect={() => go("/audit")}>Activity</Item>}
              </CommandPrimitive.Group>
            </CommandPrimitive.List>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Item({
  icon: Icon,
  onSelect,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <CommandPrimitive.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-neutral-700 aria-selected:bg-neutral-100 aria-selected:text-neutral-900"
    >
      <Icon className="size-4 text-neutral-400" />
      {children}
    </CommandPrimitive.Item>
  );
}
